import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";

/**
 * App-wide realtime subscription for calendar_events.
 * - Tenant-scoped via company_id filter (RLS provides defense-in-depth).
 * - INSERT → toast + cache invalidation.
 * - UPDATE/DELETE → silent cache invalidation.
 * - Suppresses self-toast for events the current user just created.
 */
export const useCalendarRealtime = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location.pathname);

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    (async () => {
      const { data: companyId, error } = await supabase.rpc("my_company_id");
      if (cancelled || error || !companyId) return;

      channel = supabase
        .channel(`calendar-events-realtime-${companyId}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "calendar_events",
            filter: `company_id=eq.${companyId}`,
          },
          (payload) => {
            const row: any = payload.new;
            qc.invalidateQueries({ queryKey: ["calendar-events"] });

            // Suppress toast if the current user created this event
            if (row?.created_by && row.created_by === user.id) return;

            const title: string = row?.title ?? "Untitled event";
            let dateLabel = "";
            try {
              if (row?.start_date) {
                dateLabel = format(new Date(row.start_date), "MMM d, yyyy");
                if (row?.start_time) dateLabel += ` · ${row.start_time.slice(0, 5)}`;
              }
            } catch {}

            const onCalendarPage = locationRef.current.startsWith("/controller/calendar");

            if (onCalendarPage) {
              toast.success("New event added to calendar", {
                description: dateLabel ? `${title} — ${dateLabel}` : title,
              });
            } else {
              toast("New calendar event", {
                description: dateLabel ? `${title} — ${dateLabel}` : title,
                action: {
                  label: "View",
                  onClick: () => navigate("/controller/calendar"),
                },
              });
            }
          }
        )
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "calendar_events",
            filter: `company_id=eq.${companyId}`,
          },
          () => qc.invalidateQueries({ queryKey: ["calendar-events"] })
        )
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "calendar_events",
            filter: `company_id=eq.${companyId}`,
          },
          () => qc.invalidateQueries({ queryKey: ["calendar-events"] })
        )
        .subscribe();
    })();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [user, qc, navigate]);
};
